import React, { createContext, useEffect } from 'react';
import { AuthError, UseAuthError, useAuthError } from '../use-auth-error';
import { useEventPublicInfo } from '../../../hooks/api/public/use-event-public-details';
import { ML_ACCESS_TOKEN, SUBSCRIBE_TO_EVENT } from '../../constants';
import { IAuthContext, useAuth } from '../auth-context';
import { getUrlQueryArgByKey } from '../../api/utils';
import { AppContextType, useApp } from '../app-config-context';
import { Buffer } from 'buffer';
import { authenticateWithToken, handlePassportAuthTokens } from '../../auth';
import { useHistory } from 'react-router-dom';
import { verifyMagicLinkTokenSSO } from '../../api/public';
import { PassportAuthTokens } from '../../api';

export type IPasswordlessAuthContext = {
  authError: AuthError,
  resetAuthError(): void,
}

const usePasswordlessAuthProvider = () => {
  const app = useApp() as AppContextType;
  const auth = useAuth() as IAuthContext;
  const { error: authError, setError } = useAuthError() as UseAuthError;
  const resetAuthError = () => setError(null);
  const { data } = useEventPublicInfo();

  const history = useHistory();

  const removeMLTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has(ML_ACCESS_TOKEN) && !params.has(SUBSCRIBE_TO_EVENT)) {
      params.delete(ML_ACCESS_TOKEN);

      history.replace({
        search: params.toString(),
        pathname: history.location.pathname,
        state: history.location.state,
      });
    }
  };

  const token = getUrlQueryArgByKey(ML_ACCESS_TOKEN) || '';

  async function signInWithToken(token: string): Promise<void> {
    const isSSOEnabled = data?.eventId && data?.attendeeSsoEnabled && !auth.isAuthenticated;
    auth.setPasswordlessAuthInProgress();

    let redirectToSSO = false;

    try {
      if (isSSOEnabled) {
        redirectToSSO = await handleSSOLogin(token);
      } else if (data?.eventId && auth.isAuthenticated === false) {
        await authenticateWithToken(token);
        auth.setAsAuthenticated();
      } else if (auth.isAuthenticated) {
        return;
      }
    } catch {
      auth.setAsNotAuthenticated();
      auth.setInvalidLink();
    } finally {
      if (!redirectToSSO) {
        auth.resetPasswordlessAuthInProgress();
        removeMLTokenFromUrl();
      }
    }
  }

  async function handleSSOLogin(token: string): Promise<boolean> {
    const response = await verifyMagicLinkTokenSSO(token);
    if (response.fusionAuthIdpRedirectUrl) {
      window.location.href = response.fusionAuthIdpRedirectUrl;
      return true;
    } else if (response?.accessToken) {
      handlePassportAuthTokens(response as PassportAuthTokens);
      auth.setAsAuthenticated();
    }
    return false;
  }

  useEffect(() => {
    if (token && app.isConfigured && data) {
      try {
        const tokenJsonString = Buffer.from(token, 'base64')?.toString() || '';
        JSON.parse(tokenJsonString);
        signInWithToken(token);
      } catch {
        auth.setInvalidLink();
        removeMLTokenFromUrl();
      }
    } else if (!token) {
      auth.resetPasswordlessAuthInProgress();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, app.isConfigured, auth.isAuthenticated]);

  return {
    authError, resetAuthError,
  };
};
const authContext = createContext<IPasswordlessAuthContext | void>(undefined);
type Props = {
  children: React.ReactNode | React.ReactNodeArray
}
export const PasswordlessAuthProvider: React.FC<Props> = ({ children }: Props): JSX.Element => {
  const auth = usePasswordlessAuthProvider() as IPasswordlessAuthContext;

  return (
    <authContext.Provider value={auth}>
      {children}
    </authContext.Provider>
  );
};
export default PasswordlessAuthProvider;